import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { API } from "../lib/api";

import AdminCalendar from "./admin/AdminCalendar";
import AdminOverview from "./admin/AdminOverview";
import AdminRevenue from "./admin/AdminRevenue";
import AdminSalesReport from "./admin/AdminSalesReport";
import AdminCampBundles from "./admin/AdminCampBundles";
import AdminPrivateInquiries from "./admin/AdminPrivateInquiries";
import AdminTraffic from "./admin/AdminTraffic";
import AdminClasses from "./admin/AdminClasses";
import AdminRegistrations from "./admin/AdminRegistrations";
import AdminTutorials from "./admin/AdminTutorials";
import AdminStudio from "./admin/AdminStudio";
import AdminMerch from "./admin/AdminMerch";
import AdminOrders from "./admin/AdminOrders";
import AdminEmailTemplates from "./admin/AdminEmailTemplates";
import AdminEmailLog from "./admin/AdminEmailLog";
import AdminShopOrders from "./admin/AdminShopOrders";
import AdminShop from "./admin/AdminShop";
import AdminKitCodes from "./admin/AdminKitCodes";
import AdminMemberships from "./admin/AdminMemberships";
import AdminOpenStudio from "./admin/AdminOpenStudio";
import AdminParties from "./admin/AdminParties";
import AdminMedia from "./admin/AdminMedia";
import AdminApplications from "./admin/AdminApplications";
import AdminGifts from "./admin/AdminGifts";
import AdminCoupons from "./admin/AdminCoupons";
import AdminWaitlist from "./admin/AdminWaitlist";
import AdminSettings from "./admin/AdminSettings";
import AdminGuide from "./admin/AdminGuide";
import {
  LayoutDashboard, Home, CalendarDays, Activity, DollarSign, BookOpen, GraduationCap,
  Users, Palette, PartyPopper, Clock, Sparkles, FileText, CalendarClock, UserCheck,
  BadgeCheck, Gift, Ticket, ShoppingBag, Store, Package, PlayCircle, QrCode, Megaphone,
  Image as ImageIcon, Mail, Inbox, Settings as SettingsIcon, ChevronDown, ChevronRight,
  PanelLeftClose, PanelLeftOpen, Menu, X, Plus, LifeBuoy, Receipt,
} from "lucide-react";

const NAV_GROUPS = [
  { group: "Dashboard", icon: LayoutDashboard, items: [
    { k: "overview", l: "Overview", icon: Home },
    { k: "calendar", l: "Calendar", icon: CalendarDays },
    { k: "traffic", l: "Traffic", icon: Activity },
    { k: "revenue", l: "Revenue", icon: DollarSign },
    { k: "salesreport", l: "Sales Report", icon: Receipt },
  ] },
  { group: "Bookings", icon: BookOpen, items: [
    { k: "classes", l: "Classes", icon: GraduationCap },
    { k: "registrations", l: "Camp Registrations", icon: Users },
    { k: "campweeks", l: "Camp Weeks", icon: CalendarDays },
    { k: "privateinq", l: "Private Inquiries", icon: Sparkles },
    { k: "openstudio", l: "Open Studio", icon: Palette },
    { k: "parties", l: "Parties", icon: PartyPopper, badge: "parties" },
    { k: "waitlist", l: "Waitlist", icon: Clock },
  ] },
  { group: "Creators", icon: Sparkles, items: [
    { k: "applications", l: "Creator Applications", icon: FileText },
    { k: "studio", l: "Creator Bookings", icon: CalendarClock },
  ] },
  { group: "Customers & Memberships", icon: UserCheck, items: [
    { k: "memberships", l: "Memberships", icon: BadgeCheck },
    { k: "gifts", l: "Gifts", icon: Gift },
    { k: "coupons", l: "Coupons", icon: Ticket },
  ] },
  { group: "Products & Sales", icon: ShoppingBag, items: [
    { k: "shop", l: "Shop", icon: Store },
    { k: "orders", l: "Orders", icon: Package, badge: "orders" },
    { k: "tutorials", l: "Tutorials", icon: PlayCircle },
    { k: "kitcodes", l: "Kit Codes", icon: QrCode },
  ] },
  { group: "Marketing & Content", icon: Megaphone, items: [
    { k: "media", l: "Media", icon: ImageIcon },
    { k: "emailtemplates", l: "Email Templates", icon: Mail },
    { k: "emaillog", l: "Email Log", icon: Inbox },
  ] },
  { group: "Settings", icon: SettingsIcon, items: [
    { k: "settings", l: "Site Settings", icon: SettingsIcon },
  ] },
  { group: "Help", icon: LifeBuoy, items: [
    { k: "guide", l: "Studio Guide", icon: BookOpen },
  ] },
];
const GROUP_OF = {};
NAV_GROUPS.forEach((g) => g.items.forEach((it) => { GROUP_OF[it.k] = g.group; }));

const empty = {
  cls: { title: "", description: "", image_url: "", images: [], price: 95, date: "", time: "10:00 AM", duration: "3 hours", capacity: 8, category: "Adult Classes", location: "The Cake Studio", creator_id: "", pair_price: 125, booked_out: false, active: true, slug: "", offer_table_for_four: false, table_four_price: 140, external_booking_url: "", taxable: false, private: false, ask_company: false, rsvp_by: "" },
  tut: { title: "", description: "", image_url: "", video_url: "", price: 0, duration: "1 hour", level: "Beginner", access_type: "vault", slug: "", materials: "", steps: "", troubleshooting: "", kit_id: "", published: true },
  product: { category: "cake_kit", title: "", image_url: "", price: 0, description: "", included: "", pickup: true, shipping: false, shipping_fee: 0, sizes: "", open_studio_project: "", tutorial_id: "", featured: false, active: true, taxable: true },
};

export default function Admin() {
  const [stats, setStats] = useState({});
  const [classes, setClasses] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [studio, setStudio] = useState([]);
  const [newClass, setNewClass] = useState(empty.cls);
  const [editingClassId, setEditingClassId] = useState(null);
  const [newTut, setNewTut] = useState(empty.tut);
  const [editingTutId, setEditingTutId] = useState(null);
  const [tab, setTab] = useState("overview");
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set([GROUP_OF["overview"]]));
  const selectTab = (k) => {
    setTab(k);
    setOpenGroups((prev) => new Set(prev).add(GROUP_OF[k]));
    setMobileNav(false);
  };
  const toggleGroup = (g) => setOpenGroups((prev) => {
    const n = new Set(prev);
    n.has(g) ? n.delete(g) : n.add(g);
    return n;
  });

  const [gifts, setGifts] = useState([]);
  const [settings, setSettings] = useState({});
  const [applications, setApplications] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [osProjects, setOsProjects] = useState([]);
  const [osBookings, setOsBookings] = useState([]);
  const [partyInquiries, setPartyInquiries] = useState([]);
  const [media, setMedia] = useState([]);
  const [merch, setMerch] = useState([]);
  const [shopOrders, setShopOrders] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [calEvents, setCalEvents] = useState([]);
  const [classBookings, setClassBookings] = useState([]);
  const [courtesyOpen, setCourtesyOpen] = useState(false);
  const emptyCourtesy = { class_id: "", parent_name: "", parent_email: "", parent_phone: "", children: [{ name: "", allergies: "" }] };
  const [courtesy, setCourtesy] = useState(emptyCourtesy);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [tplDraft, setTplDraft] = useState({});
  const [tplPreview, setTplPreview] = useState(null);
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState(empty.product);
  const [editingProductId, setEditingProductId] = useState(null);
  const [newProject, setNewProject] = useState({ name: "", price: 35, emoji: "", description: "", image_url: "" });
  const [expDrafts, setExpDrafts] = useState({});
  const [coupons, setCoupons] = useState([]);
  const emptyCoupon = { code: "", discount_type: "percent", discount_value: 10, max_uses: "", expires_at: "", scope: "both", membership_months: 1 };
  const [newCoupon, setNewCoupon] = useState(emptyCoupon);
  const [waitlist, setWaitlist] = useState([]);
  const [badges, setBadges] = useState({ new_parties: 0, orders_to_fulfill: 0 });
  const [limits, setLimits] = useState({});
  const [hasMore, setHasMore] = useState({});
  const [totals, setTotals] = useState({});
  const [loaded, setLoaded] = useState({});

  const PAGE = 50;
  const PAGINATED = {
    emaillog: { url: "/admin/email-logs", set: setEmailLogs },
    parties: { url: "/admin/party-inquiries", set: setPartyInquiries },
    media: { url: "/admin/media", set: setMedia },
    shoporders: { url: "/admin/shop-orders", set: setShopOrders },
    merchorders: { url: "/admin/merch-orders", set: setMerch },
  };

  const loadPaged = async (key, limit) => {
    const cfg = PAGINATED[key];
    const lim = limit ?? limits[key] ?? PAGE;
    const { data } = await api.get(`${cfg.url}?limit=${lim}`);
    cfg.set(data || []);
    setLimits((s) => ({ ...s, [key]: lim }));
    setLoaded((s) => ({ ...s, [key]: (data || []).length }));
    setHasMore((s) => ({ ...s, [key]: (data || []).length >= lim }));
  };
  const loadMore = (key) => loadPaged(key, (limits[key] ?? PAGE) + PAGE);

  const FETCHERS = {
    stats: async () => setStats((await api.get("/admin/stats")).data),
    classes: async () => setClasses((await api.get("/classes?include_past=true&include_inactive=true")).data),
    tutorials: async () => setTutorials((await api.get("/tutorials?all=true")).data),
    studio: async () => setStudio((await api.get("/admin/studio-bookings")).data),
    gifts: async () => setGifts((await api.get("/admin/gifts")).data),
    settings: async () => setSettings((await api.get("/admin/settings")).data),
    applications: async () => setApplications((await api.get("/admin/creator-applications")).data),
    memberships: async () => setMemberships((await api.get("/admin/memberships")).data),
    osprojects: async () => setOsProjects((await api.get("/admin/open-studio/projects")).data),
    osbookings: async () => setOsBookings((await api.get("/admin/open-studio/bookings")).data),
    calendar: async () => setCalEvents((await api.get("/admin/calendar")).data || []),
    classbookings: async () => setClassBookings((await api.get("/admin/class-bookings")).data || []),
    coupons: async () => setCoupons((await api.get("/admin/coupons")).data || []),
    waitlist: async () => setWaitlist((await api.get("/admin/waitlist")).data || []),
    products: async () => setProducts((await api.get("/admin/products")).data),
    emailtemplates: async () => {
      const et = (await api.get("/admin/email-templates")).data;
      setEmailTemplates(et);
      const drafts = {};
      (et || []).forEach((tpl) => { drafts[tpl.key] = { ...tpl.values }; });
      setTplDraft(drafts);
    },
  };

  // Only the data each tab actually needs — no more loading all 21 lists at once.
  const TAB_DEPS = {
    overview: ["stats"],
    calendar: ["calendar"],
    traffic: [],
    revenue: ["stats"],
    salesreport: [],
    privateinq: [],
    classes: ["classes"],
    registrations: ["classes", "classbookings", "waitlist"],
    openstudio: ["osprojects", "osbookings"],
    parties: ["parties"],
    waitlist: ["waitlist", "classes"],
    applications: ["applications"],
    studio: ["studio"],
    memberships: ["memberships"],
    gifts: ["gifts"],
    coupons: ["coupons"],
    shop: ["products"],
    orders: ["shoporders", "merchorders"],
    tutorials: ["tutorials"],
    kitcodes: ["tutorials"],
    media: ["media"],
    emailtemplates: ["emailtemplates"],
    emaillog: ["emaillog"],
    settings: ["settings"],
    guide: [],
  };

  const loadBadges = async () => {
    try { setBadges((await api.get("/admin/badge-counts")).data || {}); } catch (e) { /* noop */ }
  };
  const loadCounts = async () => {
    try { setTotals((await api.get("/admin/list-counts")).data || {}); } catch (e) { /* noop */ }
  };
  const loadKey = (key) => (PAGINATED[key] ? loadPaged(key) : (FETCHERS[key] ? FETCHERS[key]() : Promise.resolve()));

  const reload = async (targetTab) => {
    const t = targetTab || tab;
    const deps = TAB_DEPS[t] || [];
    const needsCounts = deps.some((k) => PAGINATED[k]);
    try {
      await Promise.all([loadBadges(), ...(needsCounts ? [loadCounts()] : []), ...deps.map(loadKey)]);
    } catch (e) { toast.error("Failed to load admin data"); }
  };

  const toggleMerch = async (o) => {
    const next = o.status === "fulfilled" ? "to_fulfill" : "fulfilled";
    try { await api.put(`/admin/merch-orders/${o.id}?status=${next}`); reload(); }
    catch (e) { toast.error("Failed to update order"); }
  };

  const toggleShopOrder = async (o) => {
    const next = o.status === "fulfilled" ? "to_fulfill" : "fulfilled";
    try { await api.put(`/admin/shop-orders/${o.id}?status=${next}`); reload(); }
    catch (e) { toast.error("Failed to update order"); }
  };

  const saveTemplate = async (key) => {
    try { await api.put(`/admin/email-templates/${key}`, tplDraft[key] || {}); toast.success("Email template saved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const previewTemplate = async (key) => {
    try { const { data } = await api.post(`/admin/email-templates/${key}/preview`, tplDraft[key] || {}); setTplPreview(data); }
    catch (e) { toast.error("Preview failed"); }
  };
  const resetTemplate = (tpl) => setTplDraft((d) => ({ ...d, [tpl.key]: { ...tpl.defaults } }));

  const saveProduct = async () => {
    const payload = {
      ...newProduct,
      price: Number(newProduct.price) || 0,
      shipping_fee: Number(newProduct.shipping_fee) || 0,
      tutorial_id: newProduct.tutorial_id || null,
      sizes: typeof newProduct.sizes === "string"
        ? newProduct.sizes.split(",").map((x) => x.trim()).filter(Boolean)
        : (newProduct.sizes || []),
    };
    try {
      if (editingProductId) { await api.put(`/admin/products/${editingProductId}`, payload); toast.success("Product updated"); }
      else { await api.post("/admin/products", payload); toast.success("Product added"); }
      setNewProduct(empty.product); setEditingProductId(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editProduct = (p) => {
    setEditingProductId(p.id);
    setNewProduct({ ...empty.product, ...p, taxable: p.taxable !== false, sizes: (p.sizes || []).join(", "), tutorial_id: p.tutorial_id || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEditProduct = () => { setEditingProductId(null); setNewProduct(empty.product); };
  const delProduct = async (id) => { try { await api.delete(`/admin/products/${id}`); reload(); } catch (e) { toast.error("Failed to delete"); } };

  const [kitTutorialId, setKitTutorialId] = useState("");
  const [kitCount, setKitCount] = useState(20);
  const [kitCodes, setKitCodes] = useState([]);
  const loadKitCodes = async (tid) => {
    if (!tid) { setKitCodes([]); return; }
    try { const { data } = await api.get(`/admin/kit-codes?tutorial_id=${tid}`); setKitCodes(data); } catch (e) { /* noop */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "kitcodes" && !kitTutorialId && tutorials[0]) setKitTutorialId(tutorials[0].id); }, [tab, tutorials]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (kitTutorialId) loadKitCodes(kitTutorialId); }, [kitTutorialId]);
  const generateKitCodes = async () => {
    if (!kitTutorialId) { toast.error("Pick a tutorial first"); return; }
    try {
      const { data } = await api.post("/admin/kit-codes/generate", { tutorial_id: kitTutorialId, count: Number(kitCount) || 1 });
      toast.success(`${data.codes.length} access codes generated`);
      loadKitCodes(kitTutorialId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delKitCode = async (cid) => { try { await api.delete(`/admin/kit-codes/${cid}`); loadKitCodes(kitTutorialId); } catch (e) { toast.error("Failed to delete"); } };
  const toggleKitCodeStatus = async (c) => {
    try {
      await api.patch(`/admin/kit-codes/${c.id}/status?disabled=${c.status !== "disabled"}`);
      loadKitCodes(kitTutorialId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyUnusedCodes = () => {
    const codes = kitCodes.filter((c) => c.status === "unused").map((c) => c.code).join("\n");
    navigator.clipboard.writeText(codes); toast.success("Unused codes copied");
  };
  const downloadQR = () => {
    const canvas = document.querySelector("#kit-qr-hires canvas") || document.querySelector("#kit-qr-canvas canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `cake-kit-qr-${kitTutorialId}.png`;
    a.click();
  };
  const kitTut = tutorials.find((t) => t.id === kitTutorialId);
  const kitQrUrl = kitTut ? `${window.location.origin}/tutorials/${kitTut.slug || kitTut.id}?kit=1` : "";

  const copyMediaUrl = (path) => {
    const url = `${API}/files/${path}`;
    navigator.clipboard?.writeText(url);
    toast.success("URL copied to clipboard");
  };

  const deleteMedia = async (id) => {
    if (!confirm("Remove this file from your library? Anything still using it will stop displaying.")) return;
    try { await api.delete(`/admin/media/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setInquiryStatus = async (id, status) => {
    try { await api.put(`/admin/party-inquiries/${id}?status=${status}`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createDepositLink = async (id, amount, sendToCustomer = false) => {
    const { data } = await api.post(`/admin/party-inquiries/${id}/deposit-link`, { amount: Number(amount), send_to_customer: sendToCustomer });
    reload();
    return data;
  };

  const editParty = async (id, payload) => {
    const { data } = await api.patch(`/admin/party-inquiries/${id}/edit`, payload);
    reload();
    return data;
  };

  const sendFinalDetails = async (id) => {
    const { data } = await api.post(`/admin/party-inquiries/${id}/send-final-details`);
    reload();
    return data;
  };

  const getPartyBalance = async (id) => {
    const { data } = await api.get(`/admin/party-inquiries/${id}/balance`);
    return data;
  };
  const createFinalPaymentLink = async (id, sendToCustomer = false) => {
    const { data } = await api.post(`/admin/party-inquiries/${id}/final-payment-link`, { send_to_customer: sendToCustomer });
    reload();
    return data;
  };
  const markFinalPaid = async (id) => {
    const { data } = await api.post(`/admin/party-inquiries/${id}/mark-final-paid`);
    reload();
    return data;
  };
  const reopenParty = async (id) => {
    const { data } = await api.post(`/admin/party-inquiries/${id}/reopen`);
    reload();
    return data;
  };

  const addProject = async () => {
    if (!newProject.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/admin/open-studio/projects", { ...newProject, price: Number(newProject.price) || 0, active: true });
      setNewProject({ name: "", price: 35, emoji: "", description: "", image_url: "" });
      toast.success("Experience added"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveExperience = async (p) => {
    const d = { ...p, ...(expDrafts[p.id] || {}) };
    try {
      await api.put(`/admin/open-studio/projects/${p.id}`, {
        name: d.name, price: Number(d.price) || 0, emoji: d.emoji || "",
        description: d.description || "", image_url: d.image_url || "", active: d.active,
        no_dyes: !!d.no_dyes,
      });
      toast.success("Experience saved"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleProject = async (p) => {
    try {
      await api.put(`/admin/open-studio/projects/${p.id}`, {
        name: p.name, price: Number(p.price) || 0, emoji: p.emoji || "",
        description: p.description || "", image_url: p.image_url || "", active: !p.active,
        no_dyes: !!p.no_dyes,
      });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delProject = async (id) => {
    if (!confirm("Delete this experience?")) return;
    try { await api.delete(`/admin/open-studio/projects/${id}`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setExpField = (id, key, val) => setExpDrafts((d) => ({ ...d, [id]: { ...d[id], [key]: val } }));

  const delStudioBooking = async (id) => {
    if (!confirm("Delete this studio booking?")) return;
    try { await api.delete(`/admin/studio-bookings/${id}`); toast.success("Booking deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delOsBooking = async (id) => {
    if (!confirm("Delete this Open Studio booking?")) return;
    try { await api.delete(`/admin/open-studio/bookings/${id}`); toast.success("Booking deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const decide = async (id, status) => {
    try {
      const { data } = await api.post(`/admin/creator-applications/${id}/decision`, { status, origin: window.location.origin });
      if (status === "approved") {
        toast.success(data.account === "upgraded"
          ? "Approved — existing account upgraded to creator"
          : "Approved — invite email sent to set their password");
      } else {
        toast.success("Application declined");
      }
      reload();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addCourtesy = async () => {
    if (!courtesy.class_id) { toast.error("Choose a class/camp"); return; }
    if (!courtesy.parent_name.trim()) { toast.error("Enter a name for the registration"); return; }
    const children = courtesy.children.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), allergies: (c.allergies || "").trim() }));
    try {
      const { data } = await api.post("/admin/courtesy-registration", {
        class_id: courtesy.class_id,
        parent_name: courtesy.parent_name.trim(),
        parent_email: courtesy.parent_email.trim() || null,
        parent_phone: courtesy.parent_phone.trim(),
        children,
      });
      toast.success(`Courtesy registration added (${data.num_children} ${data.num_children === 1 ? "spot" : "spots"})`);
      setCourtesy(emptyCourtesy);
      setCourtesyOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportRegistrationsCSV = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Class", "Date", "Category", "Child Name", "Allergies", "Parent Name", "Parent Phone", "Parent Email", "Amount Paid", "Booked On", "Status", "Company"];
    const rows = [];
    classBookings.forEach((b) => {
      const status = b.status === "refunded" ? "refunded" : (b.source === "courtesy" ? "comp" : (b.status || "paid"));
      const base = [b.class_title, b.class_date, b.class_category, "", "", b.guest_name || b.user_name || "Account holder", b.parent_phone || b.user_phone || "", b.guest_email || b.user_email || "", b.amount, (b.created_at || "").slice(0, 10), status, b.company_name || ""];
      const kids = b.children || [];
      if (kids.length) {
        kids.forEach((ch) => { const r = [...base]; r[3] = ch.name; r[4] = ch.allergies || "None"; rows.push(r); });
      } else {
        rows.push(base);
      }
    });
    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `camp-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resendInvite = async (id) => {
    try {
      const { data } = await api.post(`/admin/creator-applications/${id}/resend-invite`, { origin: window.location.origin });
      toast.success(`Invite re-sent to ${data.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeApp = async (id) => {
    if (!window.confirm("Delete this application? This can't be undone.")) return;
    try {
      const { data } = await api.delete(`/admin/creator-applications/${id}`);
      toast.success(data.removed_pending_account ? "Application & pending creator account deleted" : "Application deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const refundGift = async (id) => {
    if (!confirm("Refund / void this gift card? Its remaining balance will be set to $0.")) return;
    try { await api.post(`/admin/gifts/${id}/refund`); toast.success("Done"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Settings saved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveCoupon = async () => {
    if (!newCoupon.code.trim()) { toast.error("Enter a coupon code"); return; }
    const payload = {
      code: newCoupon.code.trim().toUpperCase(),
      discount_type: newCoupon.discount_type,
      discount_value: Number(newCoupon.discount_value) || 0,
      max_uses: newCoupon.max_uses ? Number(newCoupon.max_uses) : null,
      expires_at: newCoupon.expires_at || null,
      scope: newCoupon.scope || "both",
      membership_months: newCoupon.scope === "membership" ? (Number(newCoupon.membership_months) || 1) : null,
      active: true,
    };
    try { await api.post("/admin/coupons", payload); toast.success("Coupon created"); setNewCoupon(emptyCoupon); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleCoupon = async (c) => {
    try {
      await api.put(`/admin/coupons/${c.id}`, {
        code: c.code, discount_type: c.discount_type, discount_value: c.discount_value,
        max_uses: c.max_uses, expires_at: c.expires_at, scope: c.scope || "both",
        membership_months: c.membership_months, active: !c.active,
      });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delCoupon = async (id) => {
    if (!confirm("Delete this coupon?")) return;
    try { await api.delete(`/admin/coupons/${id}`); reload(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const delWaitlist = async (id) => {
    try { await api.delete(`/admin/waitlist/${id}`); reload(); }
    catch (e) { toast.error("Failed to remove"); }
  };

  const refundBooking = async (id) => {
    if (!confirm("Mark this registration as refunded?\n\nThis frees the seat(s) and removes them from the active roster. Make sure you've already issued the refund in Stripe.")) return;
    try {
      const { data } = await api.post(`/admin/class-bookings/${id}/refund`);
      const seatMsg = data.freed_seats > 1 ? `Refunded — ${data.freed_seats} seats freed` : "Registration marked refunded";
      toast.success(data.waitlist_notified > 0
        ? `${seatMsg}. Emailed ${data.waitlist_notified} waitlisted ${data.waitlist_notified === 1 ? "person" : "people"}.`
        : seatMsg);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unrefundBooking = async (id) => {
    try {
      await api.post(`/admin/class-bookings/${id}/unrefund`);
      toast.success("Registration restored to active roster");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const notifyWaitlist = async (classId) => {
    try {
      const { data } = await api.post(`/admin/classes/${classId}/notify-waitlist?origin=${encodeURIComponent(window.location.origin)}`);
      toast.success(data.notified > 0 ? `Emailed ${data.notified} waiting ${data.notified === 1 ? "person" : "people"}` : "No available seats to notify about");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    reload(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const saveClass = async () => {
    const payload = { ...newClass, price: Number(newClass.price), capacity: Number(newClass.capacity),
      pair_price: newClass.category === "Kids Camps" ? (Number(newClass.pair_price) || 0) : null,
      offer_table_for_four: newClass.category === "Adult Classes" && !!newClass.offer_table_for_four,
      table_four_price: (newClass.category === "Adult Classes" && newClass.offer_table_for_four) ? (Number(newClass.table_four_price) || 140) : null,
      booked_out: !!newClass.booked_out, active: newClass.active !== false,
      images: (newClass.images || []).filter(Boolean),
      taxable: !!newClass.taxable,
      private: !!newClass.private, ask_company: !!newClass.ask_company,
      rsvp_by: newClass.rsvp_by || null,
      external_booking_url: (newClass.external_booking_url || "").trim() || null };
    try {
      if (editingClassId) {
        await api.put(`/classes/${editingClassId}`, payload);
        toast.success("Class updated");
      } else {
        await api.post("/classes", payload);
        toast.success("Class added");
      }
      setNewClass(empty.cls);
      setEditingClassId(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editClass = (c) => {
    setEditingClassId(c.id);
    setNewClass({
      title: c.title || "", description: c.description || "", image_url: c.image_url || "", images: c.images || [],
      price: c.price ?? 95, date: c.date || "", time: c.time || "10:00 AM",
      duration: c.duration || "3 hours", capacity: c.capacity ?? 8,
      category: c.category || "Adult Classes", location: c.location || "The Cake Studio",
      creator_id: c.creator_id || "", pair_price: c.pair_price ?? 125, booked_out: !!c.booked_out, active: c.active !== false, slug: c.slug || "",
      offer_table_for_four: !!c.offer_table_for_four, table_four_price: c.table_four_price ?? 140,
      external_booking_url: c.external_booking_url || "", taxable: !!c.taxable,
      private: !!c.private, ask_company: !!c.ask_company, rsvp_by: c.rsvp_by || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEditClass = () => { setEditingClassId(null); setNewClass(empty.cls); };

  const toggleClassActive = async (c) => {
    const next = c.active === false;
    try {
      await api.patch(`/classes/${c.id}/active?active=${next}`);
      toast.success(next ? "Class is now live for registrations" : "Class hidden from your site (inactive)");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const announceClass = async (id) => {
    try {
      const { data } = await api.post(`/classes/${id}/announce`);
      toast.success(`Announcement emailed to ${data.sent} active member${data.sent === 1 ? "" : "s"}`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicateClass = async (id, date, time) => {
    if (!date) { toast.error("Pick a date for the copy"); return; }
    try {
      const q = `date=${encodeURIComponent(date)}${time ? `&time=${encodeURIComponent(time)}` : ""}`;
      await api.post(`/classes/${id}/duplicate?${q}`);
      toast.success("Class duplicated to new date");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addTut = async () => {
    const payload = { ...newTut, price: Number(newTut.price) || 0, kit_id: newTut.kit_id || null };
    try {
      if (editingTutId) { await api.put(`/tutorials/${editingTutId}`, payload); toast.success("Tutorial updated"); }
      else { await api.post("/tutorials", payload); toast.success("Tutorial added"); }
      setNewTut(empty.tut); setEditingTutId(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editTut = (t) => {
    setEditingTutId(t.id);
    setNewTut({ ...empty.tut, ...t, kit_id: t.kit_id || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEditTut = () => { setEditingTutId(null); setNewTut(empty.tut); };
  const toggleTutPublished = async (t) => {
    try {
      await api.patch(`/tutorials/${t.id}/publish?published=${!t.published}`);
      toast.success(!t.published ? "Published" : "Moved to draft");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const duplicateTut = async (id) => {
    try {
      await api.post(`/tutorials/${id}/duplicate`);
      toast.success("Duplicated as a draft");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendPasswordReset = async (email) => {
    if (!email || email === "—") { toast.error("No email on file for this member"); return; }
    try {
      await api.post("/admin/send-password-reset", { email, origin: window.location.origin });
      toast.success(`Password reset link sent to ${email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grantMembership = async (email, months) => {
    if (!email.trim()) { toast.error("Enter the member's email"); return; }
    try {
      const { data } = await api.post("/admin/grant-membership", { email: email.trim(), months: Number(months) || 12 });
      toast.success(`${data.name || data.email} now has ${data.months} free month${data.months > 1 ? "s" : ""} of membership`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendWelcomeBackfill = async () => {
    if (!confirm("Send the welcome email to all currently-active members who haven't received one yet? Each member only gets it once.")) return;
    try {
      const { data } = await api.post("/admin/send-welcome-backfill");
      toast.success(data.sent > 0 ? `Welcome email sent to ${data.sent} member${data.sent > 1 ? "s" : ""}` : "All active members have already received a welcome email");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delClass = async (id) => {
    if (!confirm("Delete this class?")) return;
    await api.delete(`/classes/${id}`); reload();
  };
  const delTut = async (id) => {
    if (!confirm("Delete this tutorial?")) return;
    await api.delete(`/tutorials/${id}`); reload();
  };

  const ctx = {
    stats, classes, tutorials, studio, delStudioBooking, newClass, setNewClass, editingClassId, newTut, setNewTut,
    editingTutId, editTut, cancelEditTut, toggleTutPublished, duplicateTut, sendPasswordReset, grantMembership, sendWelcomeBackfill,
    gifts, settings, setSettings, applications, memberships, osProjects, osBookings, delOsBooking, partyInquiries,
    media, merch, shopOrders, emailLogs, emailTemplates, calEvents, classBookings,
    courtesyOpen, setCourtesyOpen, courtesy, setCourtesy, calMonth, setCalMonth,
    tplDraft, setTplDraft, tplPreview, setTplPreview, products, newProduct, setNewProduct,
    editingProductId, newProject, setNewProject, expDrafts,
    kitTutorialId, setKitTutorialId, kitCount, setKitCount, kitCodes,
    coupons, newCoupon, setNewCoupon, saveCoupon, toggleCoupon, delCoupon,
    waitlist, delWaitlist, notifyWaitlist,
    refundBooking, unrefundBooking,
    toggleMerch, toggleShopOrder, saveTemplate, previewTemplate, resetTemplate,
    saveProduct, editProduct, cancelEditProduct, delProduct,
    generateKitCodes, delKitCode, toggleKitCodeStatus, copyUnusedCodes, downloadQR, kitQrUrl,
    copyMediaUrl, deleteMedia, setInquiryStatus, createDepositLink, editParty, sendFinalDetails, getPartyBalance, createFinalPaymentLink, markFinalPaid, reopenParty, addProject, saveExperience, toggleProject,
    delProject, setExpField, decide, addCourtesy, exportRegistrationsCSV, resendInvite, removeApp,
    refundGift, saveSettings, saveClass, editClass, cancelEditClass, addTut, delClass, delTut,
    duplicateClass, toggleClassActive, announceClass,
    openCourtesyFor: (cls) => {
      setCourtesy({ ...emptyCourtesy, class_id: cls.id });
      setCourtesyOpen(true);
      if (tab !== "registrations") selectTab("registrations");
      setTimeout(() => document.querySelector('[data-testid="courtesy-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    },
    reload, loadMore, hasMore, totals, loaded,
  };

  const badgeCount = (key) => (key === "parties" ? (badges.new_parties || 0) : key === "orders" ? (badges.orders_to_fulfill || 0) : 0);

  const quickActions = [
    { l: "Create Class", icon: GraduationCap, testid: "qa-create-class", fn: () => { cancelEditClass(); selectTab("classes"); } },
    { l: "Create Private Event", icon: Sparkles, testid: "qa-create-private-event", fn: () => { setEditingClassId(null); setNewClass({ ...empty.cls, private: true }); selectTab("classes"); } },
    { l: "Add Party", icon: PartyPopper, testid: "qa-add-party", fn: () => selectTab("parties") },
    { l: "Add Manual Booking", icon: Plus, testid: "qa-manual-booking", fn: () => { selectTab("registrations"); setCourtesyOpen(true); } },
    { l: "Create Coupon", icon: Ticket, testid: "qa-create-coupon", fn: () => selectTab("coupons") },
    { l: "View Calendar", icon: CalendarDays, testid: "qa-view-calendar", fn: () => selectTab("calendar") },
    { l: "Studio Guide", icon: LifeBuoy, testid: "qa-studio-guide", fn: () => selectTab("guide") },
  ];

  const NavList = ({ collapsed }) => (
    <nav className="space-y-1" data-testid="admin-sidebar-nav">
      {NAV_GROUPS.map((g) => {
        const groupBadge = g.items.reduce((s, it) => s + (it.badge ? badgeCount(it.badge) : 0), 0);
        const isOpen = openGroups.has(g.group);
        const GroupIcon = g.icon;
        if (collapsed) {
          return g.items.map((it) => {
            const ItIcon = it.icon;
            const b = it.badge ? badgeCount(it.badge) : 0;
            return (
              <button
                key={it.k}
                data-testid={`admin-tab-${it.k}`}
                onClick={() => selectTab(it.k)}
                title={it.l}
                className={`relative w-full flex justify-center py-2.5 rounded-lg transition-colors ${tab === it.k ? "bg-[#121212] text-[#C6FF33]" : "text-stone-500 hover:bg-[#F2E9D8] hover:text-[#121212]"}`}
              >
                <ItIcon size={18} />
                {b > 0 && <span className="absolute top-1 right-2 w-2 h-2 bg-[#E0218A] rounded-full" />}
              </button>
            );
          });
        }
        return (
          <div key={g.group}>
            <button
              data-testid={`admin-navgroup-${g.group.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
              onClick={() => toggleGroup(g.group)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs tracking-[0.12em] uppercase font-bold text-stone-500 hover:text-[#121212] transition-colors"
            >
              <GroupIcon size={15} />
              <span className="flex-1 text-left">{g.group}</span>
              {groupBadge > 0 && <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 bg-[#E0218A] text-white text-[10px] font-bold rounded-full">{groupBadge}</span>}
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            {isOpen && (
              <div className="mb-2">
                {g.items.map((it) => {
                  const ItIcon = it.icon;
                  const b = it.badge ? badgeCount(it.badge) : 0;
                  return (
                    <button
                      key={it.k}
                      data-testid={`admin-tab-${it.k}`}
                      onClick={() => selectTab(it.k)}
                      className={`w-full flex items-center gap-2.5 pl-9 pr-3 py-2 text-sm rounded-lg transition-colors ${tab === it.k ? "bg-[#121212] text-[#C6FF33] font-bold" : "text-stone-600 hover:bg-[#F2E9D8] hover:text-[#121212]"}`}
                    >
                      <ItIcon size={16} />
                      <span className="flex-1 text-left">{it.l}</span>
                      {b > 0 && <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-[#E0218A] text-white text-[11px] font-bold rounded-full">{b}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div data-testid="admin-page" className="pt-28 pb-24 fade-in">
      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 flex gap-6">
        {/* Desktop sidebar */}
        <aside className={`hidden lg:block shrink-0 transition-all duration-200 ${navCollapsed ? "w-16" : "w-64"}`}>
          <div className="sticky top-28 bg-white border-2 border-[#121212] rounded-2xl p-3 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <div className={`flex items-center mb-3 ${navCollapsed ? "justify-center" : "justify-between px-2"}`}>
              {!navCollapsed && <span className="font-serif font-black text-lg text-[#121212]">Admin</span>}
              <button data-testid="sidebar-collapse-toggle" onClick={() => setNavCollapsed((v) => !v)} title={navCollapsed ? "Expand" : "Collapse"} className="p-1.5 text-stone-500 hover:text-[#121212]">
                {navCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
              </button>
            </div>
            <NavList collapsed={navCollapsed} />
          </div>
        </aside>

        {/* Mobile drawer */}
        {mobileNav && (
          <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-mobile-drawer">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileNav(false)} />
            <div className="relative w-72 max-w-[80%] bg-white h-full overflow-y-auto p-3 border-r-2 border-[#121212]">
              <div className="flex items-center justify-between px-2 mb-3">
                <span className="font-serif font-black text-lg text-[#121212]">Admin</span>
                <button data-testid="mobile-nav-close" onClick={() => setMobileNav(false)} className="p-1.5 text-stone-500"><X size={20} /></button>
              </div>
              <NavList collapsed={false} />
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-6">
            <button data-testid="mobile-nav-open" onClick={() => setMobileNav(true)} className="lg:hidden p-2 border-2 border-[#121212] rounded-lg"><Menu size={18} /></button>
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-stone-500">Admin</p>
              <h1 className="font-serif text-3xl sm:text-4xl text-stone-900">Studio operations</h1>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-quick-actions">
            {quickActions.map((qa) => {
              const Icon = qa.icon;
              return (
                <button
                  key={qa.testid}
                  data-testid={qa.testid}
                  onClick={qa.fn}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#121212] text-[#F2E9D8] hover:bg-black rounded-full text-xs tracking-[0.12em] uppercase font-bold transition-colors"
                >
                  <Icon size={15} /> {qa.l}
                </button>
              );
            })}
          </div>

          {tab === "calendar" && <AdminCalendar ctx={ctx} />}
          {tab === "overview" && <AdminOverview ctx={ctx} />}
          {tab === "revenue" && <AdminRevenue ctx={ctx} />}
          {tab === "salesreport" && <AdminSalesReport />}
          {tab === "traffic" && <AdminTraffic />}
          {tab === "classes" && <AdminClasses ctx={ctx} />}
          {tab === "registrations" && <AdminRegistrations ctx={ctx} />}
          {tab === "campweeks" && <AdminCampBundles />}
          {tab === "privateinq" && <AdminPrivateInquiries openClassEditor={(c) => { selectTab("classes"); setTimeout(() => editClass(c), 0); }}
            newPrivateEvent={() => { setEditingClassId(null); setNewClass({ ...empty.cls, private: true }); selectTab("classes"); setTimeout(() => { window.scrollTo({ top: 0, behavior: "smooth" }); toast.success("Private event form ready — fill in the details and click Add class."); }, 100); }} />}
          {tab === "tutorials" && <AdminTutorials ctx={ctx} />}
          {tab === "studio" && <AdminStudio ctx={ctx} />}
          {tab === "orders" && <AdminOrders ctx={ctx} />}
          {tab === "merch" && <AdminMerch ctx={ctx} />}
          {tab === "emailtemplates" && <AdminEmailTemplates ctx={ctx} />}
          {tab === "emaillog" && <AdminEmailLog ctx={ctx} />}
          {tab === "shoporders" && <AdminShopOrders ctx={ctx} />}
          {tab === "shop" && <AdminShop ctx={ctx} />}
          {tab === "kitcodes" && <AdminKitCodes ctx={ctx} />}
          {tab === "memberships" && <AdminMemberships ctx={ctx} />}
          {tab === "openstudio" && <AdminOpenStudio ctx={ctx} />}
          {tab === "parties" && <AdminParties ctx={ctx} />}
          {tab === "media" && <AdminMedia ctx={ctx} />}
          {tab === "applications" && <AdminApplications ctx={ctx} />}
          {tab === "gifts" && <AdminGifts ctx={ctx} />}
          {tab === "coupons" && <AdminCoupons ctx={ctx} />}
          {tab === "waitlist" && <AdminWaitlist ctx={ctx} />}
          {tab === "settings" && <AdminSettings ctx={ctx} />}
          {tab === "guide" && <AdminGuide />}
        </div>
      </div>
    </div>
  );
}
